// Mailbox overlay launched from the dock.
// Shows MAIL entries from world.config.js. The first pinned entry is the side-quest
// hint placeholder. Other entries are filtered by `since` ISO date — visible only if
// the date is after the user's recorded last visit (or always shown if first time).
// Read state persists in localStorage so unread badges stay accurate across sessions.

import { MAIL } from '../world.config.js';

const READ_IDS_KEY = 'ian_world_mail_read_ids';
const LAST_VISIT_KEY = 'ian_world_last_visit';

function makeCloseBtn(onClick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Close');
  btn.textContent = '×';
  btn.style.cssText = `
    background: transparent;
    border: none;
    color: rgba(244, 234, 213, 0.5);
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    padding: 4px 8px;
    margin: 0;
    font-family: inherit;
    transition: color 140ms ease;
  `;
  btn.addEventListener('mouseenter', () => { btn.style.color = '#f4ead5'; });
  btn.addEventListener('mouseleave', () => { btn.style.color = 'rgba(244, 234, 213, 0.5)'; });
  btn.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return btn;
}

export class MailSystem {
  constructor() {
    this.scrim = null;
    this.panel = null;
    this.listEl = null;
    this.readerEl = null;
    this.isOpen = false;
    this.onClose = null;
    this._keyHandler = null;
    // Cache previous visit BEFORE DockSystem overwrites LAST_VISIT_KEY with the
    // current session's timestamp. This is what the visibility filter needs.
    this._lastVisit = readLastVisit();
    this._mount();
  }

  _mount() {
    const existing = document.querySelector('.iw-mail-scrim');
    if (existing) {
      this.scrim = existing;
      this.panel = existing.querySelector('.iw-mail-panel');
      this.listEl = existing.querySelector('.iw-mail-list');
      this.readerEl = existing.querySelector('.iw-mail-reader');
      this.scrim.style.display = 'none';
      return;
    }

    const scrim = document.createElement('div');
    scrim.className = 'iw-mail-scrim';
    scrim.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(8, 6, 5, 0.72);
      backdrop-filter: blur(2px);
      display: none;
      align-items: center;
      justify-content: center;
      z-index: 70;
      pointer-events: auto;
    `;

    const panel = document.createElement('div');
    panel.className = 'iw-mail-panel';
    panel.style.cssText = `
      width: min(680px, calc(100vw - 32px));
      max-height: min(560px, 80vh);
      background: #14110e;
      color: #f4ead5;
      border: 1px solid rgba(244, 234, 213, 0.18);
      border-left: 3px solid #f6b93b;
      border-radius: 6px;
      padding: 24px 28px;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65);
      display: flex;
      flex-direction: column;
      gap: 14px;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      align-items: baseline;
      justify-content: space-between;
    `;
    const title = document.createElement('h2');
    title.textContent = 'Mailbox';
    title.style.cssText = `
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 0.02em;
    `;
    const sub = document.createElement('span');
    sub.textContent = 'esc to close';
    sub.style.cssText = `
      font-size: 11px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: rgba(244, 234, 213, 0.45);
    `;
    const headerRight = document.createElement('div');
    headerRight.style.cssText = `display: flex; align-items: center; gap: 12px;`;
    headerRight.appendChild(sub);
    headerRight.appendChild(makeCloseBtn(() => this.close()));
    header.appendChild(title);
    header.appendChild(headerRight);

    const grid = document.createElement('div');
    grid.style.cssText = `
      display: grid;
      grid-template-columns: 240px 1fr;
      gap: 18px;
      flex: 1;
      min-height: 0;
    `;

    const list = document.createElement('div');
    list.className = 'iw-mail-list';
    list.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 6px;
      overflow-y: auto;
      padding-right: 6px;
    `;

    const reader = document.createElement('div');
    reader.className = 'iw-mail-reader';
    reader.style.cssText = `
      background: rgba(244, 234, 213, 0.04);
      border: 1px solid rgba(244, 234, 213, 0.1);
      border-radius: 6px;
      padding: 18px 22px;
      overflow-y: auto;
      font-size: 13px;
      line-height: 1.6;
      color: #f4ead5;
    `;
    reader.innerHTML = `<div style="color: rgba(244, 234, 213, 0.4); font-size: 12px; letter-spacing: 0.08em;">select a message →</div>`;

    grid.appendChild(list);
    grid.appendChild(reader);

    panel.appendChild(header);
    panel.appendChild(grid);

    scrim.appendChild(panel);
    document.body.appendChild(scrim);

    scrim.addEventListener('click', (event) => {
      if (event.target === scrim) this.close();
    });

    this.scrim = scrim;
    this.panel = panel;
    this.listEl = list;
    this.readerEl = reader;
  }

  open({ onClose } = {}) {
    this.onClose = onClose ?? null;
    this.isOpen = true;
    this.scrim.style.display = 'flex';
    this._renderList();
    this._bindKeys();
  }

  _renderList() {
    this.listEl.innerHTML = '';
    const visible = visibleMail(this._lastVisit);
    const readIds = readReadIds();

    if (visible.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No mail.';
      empty.style.cssText = 'color: rgba(244, 234, 213, 0.4); font-size: 12px; padding: 8px;';
      this.listEl.appendChild(empty);
      return;
    }

    visible.forEach((item, idx) => {
      const isRead = readIds.has(item.id);
      const row = document.createElement('button');
      row.type = 'button';
      row.style.cssText = `
        text-align: left;
        background: ${isRead ? 'transparent' : 'rgba(246, 185, 59, 0.08)'};
        border: 1px solid ${isRead ? 'rgba(244, 234, 213, 0.08)' : 'rgba(246, 185, 59, 0.3)'};
        border-radius: 4px;
        padding: 10px 12px;
        cursor: pointer;
        color: #f4ead5;
        font-family: ui-monospace, "SF Mono", Menlo, monospace;
        display: flex;
        flex-direction: column;
        gap: 4px;
        transition: background 120ms ease;
      `;
      row.addEventListener('mouseenter', () => { row.style.background = 'rgba(244, 234, 213, 0.08)'; });
      row.addEventListener('mouseleave', () => {
        row.style.background = isRead ? 'transparent' : 'rgba(246, 185, 59, 0.08)';
      });

      const fromEl = document.createElement('span');
      fromEl.textContent = item.from ?? '—';
      fromEl.style.cssText = `font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: ${isRead ? 'rgba(244, 234, 213, 0.5)' : '#f6b93b'};`;

      const subjEl = document.createElement('span');
      subjEl.textContent = item.subject ?? '(no subject)';
      subjEl.style.cssText = `font-size: 13px; font-weight: ${isRead ? 400 : 600};`;

      const previewEl = document.createElement('span');
      previewEl.textContent = item.preview ?? '';
      previewEl.style.cssText = `font-size: 11px; color: rgba(244, 234, 213, 0.55); line-height: 1.4;`;

      row.appendChild(fromEl);
      row.appendChild(subjEl);
      row.appendChild(previewEl);

      row.addEventListener('click', () => {
        this._openMessage(item);
        markRead(item.id);
        // Re-render list to update read styling
        this._renderList();
      });

      this.listEl.appendChild(row);

      // Auto-open the first message (usually the side-quest hint)
      if (idx === 0 && !this._initialOpened) {
        this._initialOpened = true;
        this._openMessage(item);
        markRead(item.id);
      }
    });
  }

  _openMessage(item) {
    this.readerEl.innerHTML = '';

    const from = document.createElement('div');
    from.textContent = `From  ${item.from ?? '—'}`;
    from.style.cssText = 'font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(244, 234, 213, 0.5); margin-bottom: 4px;';

    const subj = document.createElement('div');
    subj.textContent = item.subject ?? '';
    subj.style.cssText = 'font-size: 16px; font-weight: 600; margin-bottom: 16px; color: #f4ead5;';

    const body = document.createElement('div');
    body.textContent = item.body ?? item.preview ?? '';
    body.style.cssText = 'font-size: 13px; line-height: 1.7; white-space: pre-wrap; color: #f4ead5;';

    this.readerEl.appendChild(from);
    this.readerEl.appendChild(subj);
    this.readerEl.appendChild(body);
  }

  _bindKeys() {
    if (this._keyHandler) return;
    this._keyHandler = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };
    window.addEventListener('keydown', this._keyHandler);
  }

  _unbindKeys() {
    if (this._keyHandler) {
      window.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
  }

  close() {
    if (!this.isOpen) return;
    this._unbindKeys();
    this.isOpen = false;
    this.scrim.style.display = 'none';
    this._initialOpened = false;
    const cb = this.onClose;
    this.onClose = null;
    cb?.();
  }
}

// Returns the mail items the visitor should see right now.
// Pinned items are always shown. Non-pinned items appear if their `since` date
// is after the visitor's last recorded visit (or always, if no last visit).
export function visibleMail(lastVisit) {
  const lastDate = lastVisit ? new Date(lastVisit) : null;
  return MAIL.filter((item) => {
    if (item.pinned) return true;
    if (!item.since) return true;
    if (!lastDate || isNaN(lastDate.getTime())) return true;
    return new Date(item.since) > lastDate;
  });
}

export function hasUnreadMail(lastVisit) {
  const visible = visibleMail(lastVisit);
  const readIds = readReadIds();
  return visible.some((m) => !readIds.has(m.id));
}

function readReadIds() {
  try {
    const raw = window.localStorage.getItem(READ_IDS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (_) {
    return new Set();
  }
}

function markRead(id) {
  try {
    const ids = readReadIds();
    ids.add(id);
    window.localStorage.setItem(READ_IDS_KEY, JSON.stringify([...ids]));
  } catch (_) {}
}

function readLastVisit() {
  try {
    return window.localStorage.getItem(LAST_VISIT_KEY);
  } catch (_) {
    return null;
  }
}
